import { defineDocument } from '../../schema';
export const featureLoginDocument = defineDocument({
    id: 'feature:login',
    kind: 'feature',
    slug: 'login',
    route: '/discover/features/login',
    seo: {
        title: 'Discover Login Options',
        description: 'Sign in or create a free Airhead Games account with no password needed. New accounts get free Loons to start hosting party games immediately.',
    },
    hero: {
        eyebrow: [
            {
                type: 'text',
                text: 'Discover Feature',
            },
        ],
        title: [
            {
                type: 'text',
                text: 'Login Options',
            },
        ],
        description: [
            {
                type: 'text',
                text: 'Sign in or create a free Airhead Games account with no password needed. New accounts get free Loons to start hosting party games immediately.',
            },
        ],
    },
    aside: {
        showToc: true,
    },
    sections: [
        {
            id: 'login-overview',
            title: [
                {
                    type: 'text',
                    text: 'Two Ways to Log In',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'On the ',
                        },
                        {
                            type: 'link',
                            href: '/library',
                            children: [
                                {
                                    type: 'text',
                                    text: 'Library',
                                },
                            ],
                        },
                        {
                            type: 'text',
                            text: ` page, Airhead Games offers two password-free options: Email or Twitch. It's possible to use both methods if you start by logging in with email and then connecting your Twitch account afterwards (described below). While both options are fast and password-free, logging in with Twitch is the quickest way to get a game started.`,
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Login sessions last up to eight hours before timing out. If your session expires, simply log in again.',
                        },
                    ],
                },
                {
                    type: 'image',
                    imageAsset: 'featureLogin',
                    imageAlt: 'Login method chooser showing Email and Twitch options.',
                    caption: [
                        {
                            type: 'text',
                            text: 'Choose Email or Twitch after pressing the Login button on the Library screen.',
                        },
                    ],
                },
            ],
            sections: [
                {
                    id: 'email-login',
                    title: [
                        {
                            type: 'text',
                            text: 'Email Login',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'Email login uses a one-time passcode; we never ask you to create or remember a password. Each time you log in with email it generates a unique passcode. Steps for logging in with email:',
                                },
                            ],
                        },
                        {
                            type: 'list',
                            items: [
                                [
                                    {
                                        type: 'text',
                                        text: 'Enter your email address to request a passcode.',
                                    },
                                ],
                                [
                                    {
                                        type: 'text',
                                        text: 'Check your inbox for the code and enter it on the verification screen.',
                                    },
                                ],
                                [
                                    {
                                        type: 'text',
                                        text: 'If the email does not arrive, you can try to resend it once every 30 seconds. Double check you entered your email address correctly and press "Try different email" if needed.',
                                    },
                                ],
                            ],
                        },
                        {
                            type: 'image',
                            imageAsset: 'featureEmailOtp',
                            imageAlt: 'Example email containing the one-time passcode.',
                            caption: [
                                {
                                    type: 'text',
                                    text: 'Example one-time passcode email.',
                                },
                            ],
                            maxWidth: 800,
                        },
                    ],
                },
                {
                    id: 'twitch-login',
                    title: [
                        {
                            type: 'text',
                            text: 'Twitch Login',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'Selecting Twitch for login will redirect you to Twitch for authorization and then return you to Airhead Games. This is the fastest login method and gives you access to streamer features such as Poppy in Twitch chat, Going Live detection, and player access controls. See ',
                                },
                                {
                                    type: 'link',
                                    href: '/discover/features/twitch#twitch-permissions',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'Twitch Permissions',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ' for what each feature needs.',
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'If your Twitch email matches the email on an existing Airhead Games account, the two are linked automatically, so you keep your Loons and settings from the pre-existing account.',
                                },
                            ],
                        },
                    ],
                },
                {
                    id: 'link-twitch-later',
                    title: [
                        {
                            type: 'text',
                            text: 'Connect Twitch Later',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'If you sign up with email, you can connect Twitch later from the Connections screen in the sidebar of the Library page.',
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'While logged in, open the account menu in the bottom left corner of the ',
                                },
                                {
                                    type: 'link',
                                    href: '/library',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'Library',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ' screen. Press ',
                                },
                                {
                                    type: 'link',
                                    href: '/library?dialog=connections',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'Connections',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ' in the menu. Find the Twitch row and click Connect.',
                                },
                            ],
                        },
                        {
                            type: 'image',
                            imageAsset: 'featureConnections',
                            imageAlt: 'Connections account section with the Twitch connect button.',
                            caption: [
                                {
                                    type: 'text',
                                    text: 'Link Twitch from Connections section of Account menu.',
                                },
                            ],
                            maxWidth: 800,
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'Complete the Twitch authorization flow and you will return to the Library with your Twitch account linked. You can now sign in with either email or Twitch.',
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'Connecting Twitch, at sign-up or later, earns the Hello, Chat ',
                                },
                                {
                                    type: 'link',
                                    href: '/discover/features/achievements',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'achievement',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: '.',
                                },
                            ],
                        },
                    ],
                },
            ],
        },
        {
            id: 'personal-data-collected',
            title: [
                {
                    type: 'text',
                    text: 'Personal Data Collected',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'The only Personally Identifiable Information (PII) we collect when you log in is your email address and, if applicable, your Twitch ID. By logging in, you agree to our ',
                        },
                        {
                            type: 'link',
                            href: '/terms',
                            children: [
                                {
                                    type: 'text',
                                    text: 'Terms & Conditions',
                                },
                            ],
                        },
                        {
                            type: 'text',
                            text: '; please review them to understand how we handle your data.',
                        },
                    ],
                },
            ],
        },
        {
            id: 'logging-out',
            title: [
                {
                    type: 'text',
                    text: 'Logging Out',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'There are two ways to log out, from the hamburger menu or from the Library.',
                        },
                    ],
                },
                {
                    type: 'list',
                    items: [
                        [
                            {
                                type: 'text',
                                text: 'On some screens there will be an icon with three stacked horizontal lines (also known as the hamburger menu) in the ',
                            },
                            {
                                type: 'strong',
                                children: [
                                    {
                                        type: 'text',
                                        text: 'top right',
                                    },
                                ],
                            },
                            {
                                type: 'text',
                                text: ' corner of the screen. Tap it, scroll to the bottom, and press ',
                            },
                            {
                                type: 'strong',
                                children: [
                                    {
                                        type: 'text',
                                        text: 'Log out.',
                                    },
                                ],
                            },
                        ],
                        [
                            {
                                type: 'text',
                                text: 'Open the My Account section in the bottom left corner of the ',
                            },
                            {
                                type: 'link',
                                href: '/library',
                                children: [
                                    {
                                        type: 'text',
                                        text: 'Library',
                                    },
                                ],
                            },
                            {
                                type: 'text',
                                text: ' screen and press ',
                            },
                            {
                                type: 'strong',
                                children: [
                                    {
                                        type: 'text',
                                        text: 'Log out.',
                                    },
                                ],
                            },
                        ],
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: "Even if you don't manually log out, users will be automatically logged out after eight hours due to session timeout.",
                        },
                    ],
                },
            ],
        },
    ],
});
//# sourceMappingURL=login.js.map